import { stateCitiesMap, stateLinkMap, stateNeighbors } from "./data";
import StatePills from "./StatePills";

interface SEOCrossLinksProps {
  currentRoute?: string;
  state?: string;
  showCities?: boolean;
}

function getDescription(state: string): string {
  const current = stateLinkMap.get(state);
  if (!current || state === "all") {
    return "Fast Passport Center provides emergency passport and expedited passport processing across the United States. Click a state below for local acceptance facilities, processing times, and fast passport options.";
  }

  const neighbors = stateNeighbors[state] ?? [];
  const neighborNames = neighbors
    .map((abbr) => stateLinkMap.get(abbr)?.name)
    .filter((n): n is string => !!n);

  if (neighborNames.length === 0) {
    return `Fast Passport Center provides emergency passport and expedited passport processing in ${current.name}. Click a state below for local acceptance facilities, processing times, and fast passport options.`;
  }

  if (neighborNames.length === 1) {
    return `Fast Passport Center provides emergency passport and expedited passport processing in ${current.name}. We also serve travelers in nearby ${neighborNames[0]}. Click a state below for local acceptance facilities, processing times, and fast passport options.`;
  }

  const last = neighborNames.pop();
  return `Fast Passport Center provides emergency passport and expedited passport processing in ${current.name}. We also serve travelers in nearby states including ${neighborNames.join(", ")} and ${last}. Click a state below for local acceptance facilities, processing times, and fast passport options.`;
}

export default function SEOCrossLinks({ currentRoute, state = "all", showCities = true }: SEOCrossLinksProps) {
  const currentStateCities = stateCitiesMap[state] ?? [];
  const filteredCities = currentRoute
    ? currentStateCities.filter((c) => c.route !== currentRoute)
    : currentStateCities;

  return (
    <section className="bg-[#F7F8FA] border-t border-gray-200 pt-4 md:pt-5 pb-14 md:pb-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Network</p>
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-3">
            Emergency &amp; Expedited Passport Services Near You
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
            {getDescription(state)}
          </p>
        </div>

        <StatePills state={state} />

        {showCities && filteredCities.length > 0 && (
          <div className="mb-10">
            <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
              <i className="ri-map-pin-2-line text-[#F6C344]"></i>
              {stateLinkMap.get(state)?.name ?? "Your State"} &mdash; Emergency &amp; Expedited Passport Services
            </h3>
            <div className="flex flex-wrap gap-2">
              {filteredCities.map((city) => (
                <a
                  key={city.route}
                  href={city.route}
                  title={city.keywords}
                  className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                >
                  {city.name} <i className="ri-arrow-right-line text-[10px]"></i>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}